import { useMutation } from "@apollo/client";
import { useEffect, useMemo, useRef } from "react";
import { shallowEqual } from "react-redux";
import { selectAllergens, selectAppState, selectOpenedDetailDishTypeId } from "slices/appStateSlice";
import {
	selectActualSpotlightDishTypeIds,
	selectAvailableDishTypesIds,
	selectFilteredDishTypeIds,
} from "slices/dishesSlice";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import { selectActiveLocalTransaction } from "slices/transactionSlice";
import { LightingModeType } from "types";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { computeDesiredLighting } from "utils/lightingConvergence";
import { createDefaultLight, createSpotlightLight } from "utils/lightingUtils";
import { useFridgeInformation } from "./useFridgeInformation";
import { useAppSelector } from "./utilsHooks/storeHooks";

export const useConvergeLighting = () => {
	const { id: fridgeId } = useFridgeInformation();
	// `loading` is a secondary suppression while a mutation is genuinely in flight; the
	// optimistic refs below are the primary guard against subscription-echo re-fires.
	const [setMode, { loading: modeLoading }] = useMutation(SWITCH_FRIDGE_MODE_MUTATION);
	const [setSpotlight, { loading: spotlightLoading }] = useMutation(TOGGLE_SPOTLIGHT_MODE_MUTATION);

	const appState = useAppSelector(selectAppState);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const allergens = useAppSelector(selectAllergens);
	const filteredDishTypeIds = useAppSelector(selectFilteredDishTypeIds);
	const actualMode = useAppSelector(selectFridgeLightingMode);
	const actualSpotlightDishTypeIds = useAppSelector(selectActualSpotlightDishTypeIds);
	const availableDishTypeIds = useAppSelector(selectAvailableDishTypesIds);

	const hasTransaction = transaction != null;
	const transactionState = transaction?.state ?? null;

	// "What we last sent and are still waiting for the BE to echo back." Written SYNCHRONOUSLY
	// before each mutation so that a re-run of this effect inside the echo round-trip — the BE
	// streams many subscription frames while e.g. the fridge door is left open during an order —
	// does NOT re-fire the identical mutation. Without this guard the converger spammed
	// SWITCH_FRIDGE_MODE_MUTATION for the whole open-door warning window (~60s). This restores the
	// optimistic-write protection the legacy useFridgeLights/MealList effects had before the
	// converger refactor dropped it.
	const sentModeRef = useRef<LightingModeType | null>(null);
	const sentSpotlightRef = useRef<readonly string[] | null>(null);

	const desired = useMemo(
		() =>
			computeDesiredLighting({
				appState: appState,
				hasTransaction: hasTransaction,
				transactionState: transactionState,
				openDetailDishTypeId: openDetailDishTypeId,
				allergens: allergens,
				filteredDishTypeIds: filteredDishTypeIds,
			}),
		[appState, hasTransaction, transactionState, openDetailDishTypeId, allergens, filteredDishTypeIds],
	);

	useEffect(() => {
		const isMaster = getTabletType() === TabletTypeEnum.Master;
		if (!isMaster) {
			return;
		}
		if (fridgeId == null) {
			return;
		}
		if (desired.kind === "be-authoritative") {
			// Hand control back to the BE; drop pending optimistic markers so the next
			// fe-driven divergence starts from a clean slate.
			sentModeRef.current = null;
			sentSpotlightRef.current = null;
			return;
		}

		// Clear the pending mode marker once the echo has caught up, so it can never wedge the
		// next genuine transition.
		if (sentModeRef.current != null && actualMode === sentModeRef.current) {
			sentModeRef.current = null;
		}

		const modeDiverges = actualMode == null || desired.mode !== actualMode;
		// Suppress only a repeat send of the SAME target we are already awaiting an echo for (or
		// while the mutation is genuinely in flight). A change to a NEW target is never blocked.
		const modeAlreadyInFlight = sentModeRef.current === desired.mode || modeLoading;

		if (modeDiverges && !modeAlreadyInFlight) {
			sentModeRef.current = desired.mode; // optimistic write BEFORE the mutation
			setMode({
				variables: {
					input: {
						id: fridgeId,
						lighting: {
							mode: desired.mode,
						},
					},
				},
			});
		}

		// Per-dish spotlight reconciliation is only meaningful in Active mode. In StandBy
		// the fridge ignores per-dish state, and on the current BE sending a per-dish
		// payload wakes the fridge back to Active — undoing the StandBy command.
		if (desired.mode !== LightingModeType.Active) {
			sentSpotlightRef.current = null;
			return;
		}

		// Skip spotlight mutation if there is no per-dish payload to build —
		// even when actualSpotlightDishTypeIds is null (cold start), sending
		// an empty payload is pointless.
		if (availableDishTypeIds.size === 0) {
			return;
		}

		// Clear the pending spotlight marker once the echo matches what we sent.
		if (
			sentSpotlightRef.current != null &&
			actualSpotlightDishTypeIds != null &&
			shallowEqual(sentSpotlightRef.current, actualSpotlightDishTypeIds)
		) {
			sentSpotlightRef.current = null;
		}

		const spotlightDiverges =
			actualSpotlightDishTypeIds == null || !shallowEqual(desired.spotlightDishTypeIds, actualSpotlightDishTypeIds);
		// Same in-flight de-dupe as the mode channel: don't re-send the identical per-dish payload
		// while the previous one is still awaiting its echo.
		const spotlightAlreadyInFlight =
			(sentSpotlightRef.current != null && shallowEqual(sentSpotlightRef.current, desired.spotlightDishTypeIds)) ||
			spotlightLoading;

		if (spotlightDiverges && !spotlightAlreadyInFlight) {
			sentSpotlightRef.current = desired.spotlightDishTypeIds; // optimistic write BEFORE the mutation
			const desiredSet = new Set(desired.spotlightDishTypeIds);
			const types = Array.from(availableDishTypeIds).map((id) =>
				desiredSet.has(id) ? createSpotlightLight(id) : createDefaultLight(id),
			);
			setSpotlight({
				variables: {
					input: {
						id: fridgeId,
						dishes: {
							types: types,
						},
					},
				},
			});
		}
	}, [
		desired,
		actualMode,
		actualSpotlightDishTypeIds,
		availableDishTypeIds,
		fridgeId,
		setMode,
		setSpotlight,
		modeLoading,
		spotlightLoading,
	]);
};
